import React, { useMemo, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Boxes,
  Github,
  Layers3,
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import { useSiteContent } from "../SiteContentContext";
import { staggerDelay } from "../hooks/useScrollReveal";

const FEATURED_PROJECTS = ["AstraCDN", "SCALPEL", "RateLimiter"];

const PROJECT_STORIES = {
  AstraCDN: {
    category: "Infrastructure",
    kicker: "Self-hosted edge infrastructure",
    summary:
      "A Go-based content delivery network with an edge cache, control plane, web application firewall, and media transformations.",
    problem:
      "Explore how a CDN can stay fast, secure, and operable across a single server or a distributed edge cluster.",
    highlights: [
      "Request coalescing and segmented caching",
      "Route-scoped WAF and rate limiting",
      "Go microservices with Redis, NATS and PostgreSQL",
    ],
    architecture: ["Client", "Caddy ingress", "Edge cache", "Origin / S3"],
    accent: "bg-[#FFE600]",
    number: "01",
  },
  SCALPEL: {
    category: "AI / ML",
    kicker: "Explainable clinical AI",
    summary:
      "A skin-lesion analysis assistant that predicts clinically meaningful concepts before producing a diagnosis and explanation.",
    problem:
      "Make model reasoning inspectable by clinicians instead of jumping directly from a dermoscopic image to an opaque prediction.",
    highlights: [
      "Concept Bottleneck Model with seven dermoscopic concepts",
      "Activation maps, ROI masking and influence scores",
      "LLM-generated clinician-friendly explanations",
    ],
    architecture: ["Image", "Concept model", "Diagnosis head", "Explanation"],
    accent: "bg-[#FF4FD8]",
    number: "02",
  },
  RateLimiter: {
    category: "Infrastructure",
    kicker: "Concurrency-safe traffic control",
    summary:
      "A production-oriented sliding-window rate limiter built with Spring Boot, Redis sorted sets, and atomic Lua scripts.",
    problem:
      "Enforce fair request limits under concurrency without race conditions or a chain of inconsistent Redis operations.",
    highlights: [
      "Atomic sliding-window evaluation in Redis",
      "IP, token and user-based identification",
      "Standards-friendly 429 responses and limit headers",
    ],
    architecture: ["Request", "Spring filter", "Redis + Lua", "Allow / 429"],
    accent: "bg-[#35E6C2]",
    number: "03",
  },
  SecureCodeAuditor: {
    category: "Security",
    summary:
      "A multi-file code security scanner using FastAPI, LangGraph agents, and parallel analysis.",
  },
  MovieMatch: {
    category: "Product",
    summary:
      "A room-based movie discovery app with shared preferences, WebSocket sync, and live matches.",
  },
  SpeechBiometricsSystem: {
    category: "AI / ML",
    summary:
      "Speaker enrollment, identification, and verification using SpeechBrain and ECAPA-TDNN embeddings.",
  },
  TracePulse: {
    category: "Developer tools",
    summary:
      "A local API latency analyzer that breaks requests into DNS, TCP, TLS, server, and transfer timing.",
  },
  GenBlog: {
    category: "AI / ML",
    summary:
      "A research-assisted blog generator using Gemini, Wikipedia, DuckDuckGo, and Streamlit.",
  },
  ScrapeWise: {
    category: "Developer tools",
    summary:
      "A dynamic web scraper and question-answering interface powered by Selenium and Gemini.",
  },
  centrestage: {
    category: "Computer vision",
    summary:
      "Automatic camera framing with face tracking, motion detection, and smooth dynamic cropping.",
  },
  VideoSuperResolution: {
    category: "Computer vision",
    summary:
      "Image and video enhancement with ESRGAN-based 4× upscaling and artifact reduction.",
  },
  mints: {
    category: "Product",
    summary:
      "A JavaScript product experiment focused on a clean, interactive web experience.",
  },
};

const FILTERS = [
  "All",
  "Infrastructure",
  "AI / ML",
  "Product",
  "Developer tools",
  "Security",
  "Computer vision",
];

function getProjectPlaceholder(name, idx) {
  const safeName = encodeURIComponent(name || `Project ${idx + 1}`);
  return `https://ui-avatars.com/api/?name=${safeName}&background=111827&color=ffffff&size=1024&bold=true&format=png`;
}

function FeaturedProject({ project, index, isDark, isNeo }) {
  const story = project.story;
  const storyHighlights = story.highlights;
  const reversed = index % 2 === 1;
  const shellClass = isNeo
    ? "border-[3px] border-black bg-[#FFFDF0] shadow-[8px_8px_0_#000]"
    : isDark
      ? "rounded-3xl border border-gray-800 bg-[#15151d]"
      : "rounded-3xl border border-black/10 bg-white shadow-[0_24px_70px_rgba(35,28,20,0.08)]";
  const mutedClass = isNeo
    ? "text-black/70"
    : isDark
      ? "text-gray-400"
      : "text-gray-600";

  return (
    <article
      className={`stagger-child grid overflow-hidden lg:grid-cols-[0.92fr_1.08fr] ${shellClass}`}
      style={staggerDelay(index, 110)}
    >
      <div
        className={`relative min-h-[220px] overflow-hidden sm:min-h-[260px] lg:min-h-[350px] ${
          reversed ? "lg:order-2" : ""
        }`}
      >
        <img
          src={project.image}
          alt={`${project.name} interface`}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-[1.025]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
        <div
          className={`absolute left-4 top-4 flex h-11 w-11 items-center justify-center font-mono-space text-sm font-black ${
            isNeo
              ? `border-[3px] border-black text-black shadow-[4px_4px_0_#000] ${story.accent}`
              : isDark
                ? "rounded-2xl border border-white/10 bg-black/40 text-white backdrop-blur-md"
                : "rounded-2xl border border-black/10 bg-white/60 text-gray-900 shadow-sm backdrop-blur-md"
          }`}
        >
          {story.number}
        </div>
      </div>

      <div
        className={`flex flex-col justify-between gap-5 p-5 sm:p-6 lg:p-7 ${
          reversed ? "lg:order-1" : ""
        }`}
      >
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span
              className={`border px-3 py-1 font-mono-space text-[10px] font-bold uppercase tracking-[0.16em] ${
                isNeo
                  ? `${story.accent} border-black text-black`
                  : isDark
                    ? "rounded-full border-gray-700 bg-white/5 text-gray-300"
                    : "rounded-full border-gray-200 bg-gray-50 text-gray-600"
              }`}
            >
              {story.category}
            </span>
            <span className={`font-mono-space text-[10px] uppercase tracking-[0.14em] ${mutedClass}`}>
              {story.kicker}
            </span>
          </div>

          <h3
            className={`text-2xl font-black tracking-[-0.045em] sm:text-3xl ${
              isDark ? "text-white" : "text-gray-950"
            }`}
          >
            {project.displayName}
          </h3>
          <p
            className={`mt-2.5 max-w-xl text-sm font-medium leading-relaxed sm:text-base ${
              isDark ? "text-gray-200" : "text-gray-800"
            }`}
          >
            {story.summary}
          </p>

          <div
            className={`my-4 h-px ${isNeo ? "bg-black" : isDark ? "bg-gray-800" : "bg-gray-200"}`}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className={`mb-2 font-mono-space text-[10px] font-bold uppercase tracking-[0.18em] ${mutedClass}`}>
                The challenge
              </p>
              <p className={`text-[13px] leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {story.problem}
              </p>
            </div>
            <div>
              <p className={`mb-2 font-mono-space text-[10px] font-bold uppercase tracking-[0.18em] ${mutedClass}`}>
                Engineering highlights
              </p>
              <ul className={`space-y-1.5 text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {storyHighlights.map((highlight) => (
                  <li key={highlight} className="flex gap-2">
                    <span className="mt-[0.55em] h-1.5 w-1.5 flex-none bg-red-600" />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div>
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors ${
              isNeo
                ? `${story.accent} border-[3px] border-black text-black shadow-[4px_4px_0_#000]`
                : isDark
                  ? "rounded-xl border border-gray-700 bg-white text-black hover:bg-gray-200"
                  : "rounded-xl bg-gray-950 text-white hover:bg-red-600"
            }`}
          >
            <Github size={16} />
            Explore repository
            <ArrowUpRight size={15} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function ProjectsSection() {
  const { isDark, isNeo } = useTheme();
  const { content, isDbLoaded } = useSiteContent();
  const [activeFilter, setActiveFilter] = useState("All");
  const projectConfig = content.projects;

  const projectTitle = projectConfig?.title || "What I've been building";
  const projectRepoUrl = projectConfig?.repoUrl || "https://github.com/amandeepsingh29";
  const projects = useMemo(
    () =>
      (projectConfig?.items || []).map((project, idx) => {
        let displayName = project.name.replace(/([a-z])([A-Z])/g, "$1 $2");
        displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
        
        return {
          ...project,
          displayName,
          image: (project.image || "").trim() || getProjectPlaceholder(project.name, idx),
          story: {
            category: "Other",
            summary: project.description || "An engineering project from my GitHub workspace.",
            ...(PROJECT_STORIES[project.name] || {}),
          },
        };
      }),
    [projectConfig]
  );

  const featuredProjects = useMemo(
    () =>
      FEATURED_PROJECTS.map((name) => projects.find((project) => project.name === name)).filter(Boolean),
    [projects]
  );

  const remainingProjects = useMemo(
    () => projects.filter((project) => !FEATURED_PROJECTS.includes(project.name)),
    [projects]
  );

  const visibleProjects = useMemo(
    () =>
      activeFilter === "All"
        ? remainingProjects
        : remainingProjects.filter((project) => project.story.category === activeFilter),
    [activeFilter, remainingProjects]
  );

  const availableFilters = FILTERS.filter(
    (filter) =>
      filter === "All" ||
      remainingProjects.some((project) => project.story.category === filter)
  );

  if (!isDbLoaded || projects.length === 0) return null;

  const lastVisibleProject = visibleProjects.length - 1;
  const mutedClass = isNeo
    ? "text-black/65"
    : isDark
      ? "text-gray-500"
      : "text-gray-500";

  return (
    <section id="projects" className="relative overflow-hidden py-16 md:py-20">
      <div
        className="revealed relative z-10 mx-auto max-w-6xl px-6"
      >
        <header className="mb-10 grid gap-5 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <p className="mb-3 font-mono-space text-[11px] font-bold uppercase tracking-[0.2em] text-red-600">
              [ SELECTED WORK ]
            </p>
            <h2
              className={`max-w-3xl text-3xl font-black tracking-[-0.055em] sm:text-4xl lg:text-5xl ${
                isDark ? "text-white" : "text-gray-950"
              }`}
            >
              Projects with a story behind them.
            </h2>
            <p className={`mt-3 max-w-2xl text-sm leading-relaxed sm:text-base ${mutedClass}`}>
              Not just screenshots—these are the problems, design decisions, and systems behind what I build.
            </p>
          </div>

          <a
            href={projectRepoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex w-fit items-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors ${
              isNeo
                ? "border-[3px] border-black bg-[#35E6C2] text-black shadow-[4px_4px_0_#000]"
                : isDark
                  ? "rounded-xl border border-gray-700 text-gray-200 hover:border-red-500 hover:bg-red-500/10"
                  : "rounded-xl border border-gray-300 bg-white text-gray-800 hover:border-red-600 hover:text-red-600 hover:bg-red-50"
            }`}
          >
            <Boxes size={16} />
            View all repositories
            <ArrowUpRight size={15} />
          </a>
        </header>

        <div className="space-y-6">
          {featuredProjects.map((project, index) => (
            <FeaturedProject
              key={project.name}
              project={project}
              index={index}
              isDark={isDark}
              isNeo={isNeo}
            />
          ))}
        </div>

        <div className="mt-16">
          <div className="mb-6 flex flex-col gap-4 border-b border-current/15 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 font-mono-space text-[10px] font-bold uppercase tracking-[0.18em] text-red-600">
                [ PROJECT INDEX ]
              </p>
              <h3 className={`text-3xl font-black tracking-tight ${isDark ? "text-white" : "text-gray-950"}`}>
                More experiments &amp; builds
              </h3>
            </div>
            <p className={`font-mono-space text-xs ${mutedClass}`}>
              {String(visibleProjects.length).padStart(2, "0")} projects
            </p>
          </div>

          <div className="mb-7 flex flex-wrap gap-2" aria-label="Filter projects">
            {availableFilters.map((filter) => {
              const isActive = activeFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-3 py-1.5 font-mono-space text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    isNeo
                      ? isActive
                        ? "border-[3px] border-black bg-[#FFE600] text-black shadow-[3px_3px_0_#000]"
                        : "border-[3px] border-black bg-white text-black"
                      : isActive
                        ? "rounded-full border border-red-600 bg-red-600 text-white"
                        : isDark
                          ? "rounded-full border border-gray-800 text-gray-400 hover:border-gray-600 hover:text-white"
                          : "rounded-full border border-gray-300 bg-white text-gray-600 hover:border-gray-500 hover:text-gray-950"
                  }`}
                >
                  {filter}
                </button>
              );
            })}
          </div>

          <div
            className={`overflow-hidden border ${
              isNeo
                ? "border-[3px] border-black bg-white shadow-[6px_6px_0_#000]"
                : isDark
                  ? "rounded-2xl border-gray-800 bg-[#15151d]"
                  : "rounded-2xl border-gray-200 bg-white shadow-sm"
            }`}
          >
            <div
              className={`hidden grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_minmax(130px,0.7fr)_36px] gap-5 border-b px-5 py-3 font-mono-space text-[9px] font-bold uppercase tracking-[0.16em] md:grid ${
                isNeo
                  ? "border-black bg-[#FFE600] text-black"
                  : isDark
                    ? "border-gray-800 bg-black/20 text-gray-600"
                    : "border-gray-200 bg-gray-50 text-gray-400"
              }`}
            >
              <span>Project</span>
              <span>What it does</span>
              <span>Category / stack</span>
              <span aria-hidden="true" />
            </div>

            {visibleProjects.length > 0 ? (
              visibleProjects.map((project, index) => (
                <a
                  key={project.name}
                  href={project.url || projectRepoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group grid gap-3 px-5 py-5 transition-colors md:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_minmax(130px,0.7fr)_36px] md:items-center md:gap-5 ${
                    index !== lastVisibleProject
                      ? isNeo
                        ? "border-b-[3px] border-black"
                        : isDark
                          ? "border-b border-gray-800"
                          : "border-b border-gray-100"
                      : ""
                  } ${
                    isNeo
                      ? "hover:bg-[#35E6C2]"
                      : isDark
                        ? "hover:bg-white/[0.035]"
                        : "hover:bg-[#fffdf5]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-mono-space text-[10px] ${mutedClass}`}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h4 className={`text-base font-bold ${isDark ? "text-white" : "text-gray-950"}`}>
                      {project.displayName}
                    </h4>
                  </div>
                  <p className={`text-sm leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {project.story.summary}
                  </p>
                  <div>
                    <p className={`font-mono-space text-[10px] font-bold uppercase tracking-[0.1em] ${mutedClass}`}>
                      {project.story.category}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      {project.lang}
                    </p>
                  </div>
                  <div
                    className={`flex h-8 w-8 items-center justify-center justify-self-end border transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${
                      isNeo
                        ? "border-black bg-[#FF4FD8] text-black"
                        : isDark
                          ? "rounded-full border-gray-700 text-gray-300"
                          : "rounded-full border-gray-300 text-gray-700"
                    }`}
                  >
                    <ArrowUpRight size={15} />
                  </div>
                </a>
              ))
            ) : (
              <div className="px-5 py-12 text-center">
                <Layers3 className={`mx-auto mb-3 ${mutedClass}`} size={24} />
                <p className={`text-sm ${mutedClass}`}>No projects in this category yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
